import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Drawer,
  Divider,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import { DeleteConfirmationDialog } from "../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { BillOfMaterial } from "./types";
import { bomService } from "../../../services/bom.service";
import { BOMDetailsDrawer } from "./BOMDetailsDrawer";
import { AppTable } from "../../../../../components/AppTable";
import { AppSearch } from "../../../../../components/AppSearch";
import { FilterGroup, TableColumn } from "../../../../../app.interface";
import dayjs from "dayjs";
import { hasPermission } from "../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../auth/hooks/useAuth";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bom-tabpanel-${index}`}
      aria-labelledby={`bom-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const BOMManager: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [boms, setBoms] = React.useState<BillOfMaterial[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [sortField, setSortField] = React.useState<string>("created_at");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [totalItems, setTotalItems] = React.useState(0);
  const [selectedBOM, setSelectedBOM] = React.useState<BillOfMaterial | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [filters, setFilters] = React.useState<Record<string, any>>({});

  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: "fiber_manual_record",
      description: "Filter BOMs by their current status",
      options: [
        {
          id: "active",
          label: "Active",
          value: "active",
          icon: "check_circle",
          color: "#059669",
        },
        {
          id: "inactive",
          label: "Inactive",
          value: "inactive",
          icon: "cancel",
          color: "#DC2626",
        },
      ],
    },
    {
      id: "date_field",
      label: "Date Field",
      type: "select",
      icon: "event",
      description: "Choose which date to filter by",
      options: [
        {
          id: "created_at",
          label: "Created Date",
          value: "created_at",
          icon: "add_circle",
          color: "#0891B2",
        },
        {
          id: "updated_at",
          label: "Updated Date",
          value: "updated_at",
          icon: "update",
          color: "#0891B2",
        },
      ],
    },
    {
      id: "sortBy",
      label: "Sort By",
      type: "select",
      icon: "sort",
      description: "Sort BOMs by field",
      options: [
        {
          id: "name_asc",
          label: "Name (A-Z)",
          value: "name_asc",
          icon: "arrow_upward",
        },
        {
          id: "name_desc",
          label: "Name (Z-A)",
          value: "name_desc",
          icon: "arrow_downward",
        },
        {
          id: "created_asc",
          label: "Created (Oldest)",
          value: "created_asc",
          icon: "schedule",
        },
        {
          id: "created_desc",
          label: "Created (Newest)",
          value: "created_desc",
          icon: "update",
        },
      ],
    },
  ];

  const MasterColumns: TableColumn[] = [
    {
      id: "name",
      header: "BOM Details",
      field: "name",
      type: "action",
      width: "250px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
          {row.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {row.description}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Last updated: {dayjs(row.updated_at).format("MMM D, YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      id: "ingredients",
      header: "Ingredients & Items",
      field: "ingredients",
      type: "action",
      width: "200px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>inventory_2</Icon>
            {row.ingredients.length} ingredients
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>inventory</Icon>
            {row.processed_items.length} processed items
          </Box>
        </Stack>
      ),
    },
    {
      id: "steps",
      header: "Preparation Details",
      field: "preparation_steps",
      type: "action",
      width: "200px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#0891B2", 0.08),
              color: "#0891B2",
              border: "1px solid",
              borderColor: alpha("#0891B2", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>format_list_numbered</Icon>
            {row.preparation_steps.length} steps
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha("#F59E0B", 0.08),
              color: "#F59E0B",
              border: "1px solid",
              borderColor: alpha("#F59E0B", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>schedule</Icon>
            Shelf Life: {row.shelf_life_duration} days
          </Box>
        </Stack>
      ),
    },
    {
      id: "status",
      header: "Status",
      field: "is_active",
      type: "action",
      width: "150px",
      renderAction: (row: any) => (
        <Stack spacing={1}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: row.is_active
                ? alpha("#059669", 0.08)
                : alpha("#DC2626", 0.08),
              color: row.is_active ? "#059669" : "#DC2626",
              border: "1px solid",
              borderColor: row.is_active
                ? alpha("#059669", 0.2)
                : alpha("#DC2626", 0.2),
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>
              {row.is_active ? "check_circle" : "cancel"}
            </Icon>
            {row.is_active ? "Active" : "Inactive"}
          </Box>
        </Stack>
      ),
    },
    {
      id: "actions",
      header: "",
      field: "actions",
      type: "action",
      width: "100px",
      renderAction: (row: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit BOM" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.bom.edit",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#0891B2", 0.08),
                color: "#0891B2",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#0891B2", 0.12),
                  boxShadow: "0 4px 8px rgba(8, 145, 178, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>edit_outlined</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete BOM" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDeleteDialog(row);
              }}
              disabled={
                !hasPermission(
                  "inventory.bom.delete",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
              sx={{
                width: 34,
                height: 34,
                bgcolor: alpha("#DC2626", 0.08),
                color: "#DC2626",
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: alpha("#DC2626", 0.12),
                  boxShadow: "0 4px 8px rgba(220, 38, 38, 0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }}>delete_outlined</Icon>
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const loadBOMs = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await bomService.listBOMs({
        searchQuery: search || undefined,
        ...filters,
        sortField,
        sortOrder,
        pageIndex,
        pageSize,
      });

      setBoms(response.data);
      setTotalItems(response.metadata.total.value);
    } catch (error) {
      console.error("Error loading BOMs:", error);
      toast.error("Failed to load BOMs");
    } finally {
      setLoading(false);
    }
  }, [search, filters, sortField, sortOrder, pageIndex, pageSize]);

  React.useEffect(() => {
    loadBOMs();
  }, [loadBOMs]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleEdit = (bom: BillOfMaterial) => {
    navigate(`/business/menu/bom/${bom._id}/edit`);
  };

  const handleDelete = async () => {
    if (!selectedBOM) return;
    try {
      setSaving(true);
      const response = await bomService.deleteBOM(selectedBOM._id!);
      if (response.success) {
        toast.success("BOM deleted successfully");
        handleCloseDeleteDialog();
        loadBOMs();
      } else {
        toast.error("Failed to delete BOM");
      }
    } catch (error) {
      console.error("Error deleting BOM:", error);
      toast.error("Failed to delete BOM");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (bom: BillOfMaterial) => {
    setSelectedBOM(bom);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedBOM(null);
    setDeleteDialogOpen(false);
  };

  const handleDetailsOpen = (bom: BillOfMaterial) => {
    setSelectedBOM(bom);
    setDetailsDrawerOpen(true);
  };

  const handleDetailsClose = () => {
    setSelectedBOM(null);
    setDetailsDrawerOpen(false);
  };

  const handleAddBOM = () => {
    navigate("/business/menu/bom/new");
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Bill of Materials"
        subtitle="Manage your menu items' bill of materials"
        icon="inventory"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={handleAddBOM}
            disabled={
              !hasPermission(
                "inventory.bom.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
            sx={{
              bgcolor: "#0891B2",
              "&:hover": {
                bgcolor: "#0E7490",
              },
            }}
          >
            Add BOM
          </Button>
        }
      />

      {/* <Box sx={{ borderBottom: 1, borderColor: alpha("#0891B2", 0.1), mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="bom views"
          sx={{
            "& .MuiTab-root": {
              minHeight: 64,
              py: 2,
              color: "text.secondary",
            },
            "& .Mui-selected": {
              color: "#0891B2",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#0891B2",
            },
          }}
        >
          <Tab
            icon={<Icon>view_list</Icon>}
            label="List View"
            iconPosition="start"
          />
          <Tab
            icon={<Icon>account_tree</Icon>}
            label="Graph View"
            iconPosition="start"
          />
        </Tabs>
      </Box> */}

      <TabPanel value={selectedTab} index={0}>
        <Paper
          sx={{
            p: 3,
            mb: 3,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          }}
          elevation={0}
        >
          <AppSearch
            placeholder="Search BOMs by name or description..."
            maxWidth="100%"
            onSearch={setSearch}
            onFilterChange={setFilters}
            filterGroups={filterGroups}
            defaultFilters={filters}
          />
        </Paper>

        <Paper
          sx={{
            p: 3,
            mb: 3,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid",
            borderColor: alpha("#0891B2", 0.1),
          }}
          elevation={0}
        >
          <AppTable
            data={boms}
            columns={MasterColumns}
            loading={loading}
            sortConfig={{ field: sortField, direction: sortOrder }}
            onSort={handleSort}
            pagination={{
              total: totalItems,
              page: pageIndex,
              pageSize: pageSize,
              onPageChange: setPageIndex,
              onPageSizeChange: setPageSize,
            }}
            header={{
              title: "Bill of Materials List",
              subtitle: "Your current list of BOMs",
            }}
            onRowClick={(row) => handleDetailsOpen(row as BillOfMaterial)}
            styleOptions={{
              bordered: true,
              columnSeparators: true,
              headerBackground: alpha("#0891B2", 0.02),
              rowHoverBackground: alpha("#0891B2", 0.04),
              borderColor: alpha("#0891B2", 0.1),
            }}
            toolbarActions={{
              refresh: {
                label: "Refresh List",
                is_disabled: loading,
                onClick: loadBOMs,
              },
              export: {
                label: "Export BOMs",
                is_disabled: loading,
                options: {
                  formats: ["csv", "excel", "json"],
                  filename: "boms-list",
                },
              },
            }}
            emptyState={{
              icon: "inventory",
              title: "No BOMs Found",
              description: search
                ? "No bill of materials match your search criteria. Try adjusting your filters or search term."
                : "Get started by adding your first bill of materials to manage your menu items effectively.",
              actionLabel: "Add BOM",
              onAction: handleAddBOM,
            }}
          />
        </Paper>
      </TabPanel>

      <TabPanel value={selectedTab} index={1}>
        <Box sx={{ p: 4 }}>
          <EmptyState
            icon="account_tree"
            title="BOM Graph View"
            description="This feature is coming soon. You'll be able to visualize BOM relationships and ingredient flows."
            height={300}
          />
        </Box>
      </TabPanel>

      {/* Details Drawer */}
      {detailsDrawerOpen && selectedBOM && (
        <BOMDetailsDrawer
          open={detailsDrawerOpen}
          onClose={handleDetailsClose}
          bom={selectedBOM}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        title="Delete BOM"
        itemName={selectedBOM?.name || ""}
        itemDescription={selectedBOM?.description || ""}
        loading={saving}
        icon="inventory"
      />
    </Box>
  );
};
